import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Loader2, CheckCircle2, XCircle, AlertCircle, ArrowLeft, 
  Clock, Timer, TrendingUp, History, ChevronLeft, ChevronRight,
  BarChart3, Target, BookOpen, Flag
} from "lucide-react";
import { createManagedRequest } from "@/utils/requestManager";

// Subject topics for random selection (internal use)
const SUBJECT_TOPICS = {
  GS1: ["Indian Freedom Movement", "Ancient India", "Medieval India", "World History", "Indian Geography", "Indian Society"],
  GS2: ["Fundamental Rights", "Parliament", "Judiciary", "Constitutional Bodies", "Governance", "International Relations"],
  GS3: ["Indian Economy", "Fiscal Policy", "Agriculture", "Environment", "Science & Technology", "Internal Security"],
  GS4: ["Ethics in Public Administration", "Emotional Intelligence", "Attitude", "Aptitude", "Case Studies"],
  CSAT: ["Logical Reasoning", "Data Interpretation", "Comprehension", "Decision Making", "Basic Numeracy"],
};

const PrelimsMCQPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(""); // NEW: Loading progress message
  const [topic, setTopic] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [mcqType, setMcqType] = useState("single");
  const [numQuestions, setNumQuestions] = useState(10);
  const [result, setResult] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  
  // NEW: Timer state
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timePerQuestion, setTimePerQuestion] = useState(120); // 2 minutes default
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);
  
  // NEW: Current question index for navigation
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [reportedQuestions, setReportedQuestions] = useState(new Set());
  
  // NEW: Performance history
  const [performanceHistory, setPerformanceHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Request tracking
  const currentRequestRef = useRef(null);

  const isPro = user?.role === "PRO";
  
  const handleReportQuestion = async (question, idx) => {
    const token = localStorage.getItem("token");
    try {
      await fetch(`${API_BASE}/universal-bank/report-question`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          mcq_id: question.id || null,
          question_text: question.question?.substring(0, 200),
          reason: "incorrect_answer",
          details: `User reported incorrect answer. Topic: ${topic}, MCQ Type: ${mcqType}`
        })
      });
      setReportedQuestions(prev => new Set([...prev, idx]));
      toast({ title: "Reported", description: "Thank you! We'll review this question." });
    } catch {
      toast({ title: "Error", description: "Failed to report. Please try again.", variant: "destructive" });
    }
  };
  
  // FREE user daily limit tracking
  const [dailyUsage, setDailyUsage] = useState(0);
  const FREE_DAILY_LIMIT = 1;
  
  // Check daily usage on mount
  useEffect(() => {
    if (!isPro) {
      const today = new Date().toISOString().split('T')[0];
      const usageKey = `mcq_daily_usage_${today}`;
      const savedUsage = localStorage.getItem(usageKey);
      setDailyUsage(savedUsage ? parseInt(savedUsage) : 0);
      
      // Clean up old usage keys
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('mcq_daily_usage_') && key !== usageKey) {
          localStorage.removeItem(key);
        }
      });
    }
  }, [isPro]);

  // Load performance history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('mcq_performance_history');
    if (saved) {
      setPerformanceHistory(JSON.parse(saved));
    }
  }, []);

  // Timer effect
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleAutoSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [timerActive]);

  // Auto submit when timer runs out
  const handleAutoSubmit = () => {
    setTimerActive(false);
    toast({
      variant: "destructive",
      description: "⏰ Time's up! Auto-submitting your answers..."
    });
    setTimeout(() => handleSubmit(), 500);
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        currentRequestRef.current.cancel();
      }
      clearInterval(timerRef.current);
    };
  }, []);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a topic"
      });
      return;
    }

    // Check daily limit for FREE users
    if (!isPro && dailyUsage >= FREE_DAILY_LIMIT) {
      toast({
        variant: "destructive",
        title: "Daily Limit Reached",
        description: "FREE users can generate 1 MCQ set per day. Upgrade to PRO for unlimited access!"
      });
      navigate("/upgrade");
      return;
    }

    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.cancel();
    }
    
    // Create new managed request
    const managedRequest = createManagedRequest('mcq-generation');
    currentRequestRef.current = managedRequest;

    setLoading(true);
    setLoadingMessage("Generating UPSC-quality MCQs...");
    setResult(null);
    setUserAnswers({});
    setShowAnswers(false);
    setCurrentQuestionIndex(0);
    setTimerActive(false);

    // Update loading messages to set proper expectations
    const loadingTimer1 = setTimeout(() => {
      setLoadingMessage("Gathering latest data & crafting questions...");
    }, 8000);
    
    // Update loading message after 20 seconds
    const loadingTimer2 = setTimeout(() => {
      setLoadingMessage("Creating detailed explanations (this may take up to 1 minute)...");
    }, 20000);
    
    // Update loading message after 40 seconds
    const loadingTimer3 = setTimeout(() => {
      setLoadingMessage("Almost there! Complex topics take a bit longer...");
    }, 40000);
    
    // Additional message after 60 seconds
    const loadingTimer4 = setTimeout(() => {
      setLoadingMessage("Still working... please don't refresh. Heavy topics need more time.");
    }, 60000);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/mcq/generate`,
        {
          topic,
          difficulty,
          mcq_type: mcqType,
          num_questions: numQuestions,
          include_trap_analysis: isPro
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: managedRequest.signal,
          timeout: 180000  // 180 second (3 min) timeout for MCQ generation
        }
      );

      const data = response.data;
      
      // Check for error response from backend
      if (data.error) {
        toast({
          variant: "destructive",
          description: data.error
        });
        setResult(null);
        return;
      }
      
      // Check if we got any questions
      if (!data.questions || data.questions.length === 0) {
        toast({
          variant: "destructive",
          description: "No MCQs generated. Please try a different topic or try again."
        });
        setResult(null);
        return;
      }
      
      setResult(data);
      managedRequest.cleanup();
      
      // Increment daily usage for FREE users
      if (!isPro) {
        const today = new Date().toISOString().split('T')[0];
        const usageKey = `mcq_daily_usage_${today}`;
        const newUsage = dailyUsage + 1;
        localStorage.setItem(usageKey, newUsage.toString());
        setDailyUsage(newUsage);
      }
      
      // Start timer if enabled
      if (timerEnabled) {
        const totalTime = timePerQuestion * (data.questions?.length || numQuestions);
        setTimeRemaining(totalTime);
        setTimerActive(true);
      }
      
      // Track activity for "Continue where you left off"
      try {
        await managedAxios.post(
          `${API_BASE}/command/track-activity?activity_type=mcq&activity_name=MCQ Practice - ${topic}&activity_path=/prelims/mcq-practice`,
          null,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.log("Activity tracking skipped");
      }
      
      // Show success message with actual count
      const questionCount = data.questions?.length || 0;
      const requestedCount = numQuestions;
      if (questionCount < requestedCount) {
        toast({
          description: `Generated ${questionCount} MCQs (${requestedCount} requested). Some topics may have limited content.`
        });
      } else {
        toast({
          description: `Generated ${questionCount} MCQs successfully!`
        });
      }
    } catch (err) {
      // Don't show error if request was cancelled
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error("Error:", err);
      
      // Better error messages with retry suggestion
      let errorMessage = "Failed to generate MCQs";
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = "Generation is taking longer than usual. Please try with 5 questions or a simpler topic like 'Fundamental Rights'.";
      } else if (err.response?.status === 504 || err.response?.status === 502) {
        errorMessage = "Server is busy. Please wait a moment and try again with fewer questions.";
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast({
        variant: "destructive",
        description: errorMessage
      });
    } finally {
      setLoading(false);
      setLoadingMessage("");
      // Clear loading message timers
      clearTimeout(loadingTimer1);
      clearTimeout(loadingTimer2);
      clearTimeout(loadingTimer3);
      clearTimeout(loadingTimer4);
      currentRequestRef.current = null;
    }
  };

  const handleAnswerSelect = (questionIdx, answer) => {
    setUserAnswers({
      ...userAnswers,
      [questionIdx]: answer
    });
  };

  const handleSubmit = async () => {
    setShowAnswers(true);
    setTimerActive(false);
    clearInterval(timerRef.current);
    
    // Save to performance history
    const score = calculateScore();
    const historyEntry = {
      id: Date.now(),
      date: new Date().toISOString(),
      topic,
      difficulty,
      score: score.correct,
      total: score.total,
      percentage: Math.round((score.correct / score.total) * 100),
      timeUsed: timerEnabled ? (timePerQuestion * numQuestions) - timeRemaining : null
    };
    
    const newHistory = [historyEntry, ...performanceHistory].slice(0, 20); // Keep last 20
    setPerformanceHistory(newHistory);
    localStorage.setItem('mcq_performance_history', JSON.stringify(newHistory));
    
    // Track for daily targets and selection probability
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Mark MCQ target as complete for daily targets
        const todayRes = await fetch(`${API_BASE}/daily-targets/today`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (todayRes.ok) {
          const todayData = await todayRes.json();
          await fetch(`${API_BASE}/daily-targets/mark-complete?target_id=${todayData.id}&completion_type=mcqs`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        
        // Track MCQ result for Selection Probability calculation
        const timeUsed = timerEnabled ? (timePerQuestion * numQuestions) - timeRemaining : 0;
        await fetch(`${API_BASE}/prelims/track-mcq-result?topic=${encodeURIComponent(topic)}&difficulty=${difficulty}&correct=${score.correct}&total=${score.total}&time_taken=${timeUsed}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // ═══════════════════════════════════════════════════════════════════
        // TRACK WRONG ANSWERS TO MISTAKE BANK
        // ═══════════════════════════════════════════════════════════════════
        if (result?.questions) {
          for (let i = 0; i < result.questions.length; i++) {
            const q = result.questions[i];
            const userAnswer = userAnswers[i];
            const isCorrect = userAnswer === q.correct_answer;
            
            if (!isCorrect && userAnswer !== undefined) {
              // Add to mistake bank
              await fetch(`${API_BASE}/mistakes/add`, {
                method: 'POST',
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  question_id: `prelims-mcq-${topic}-${Date.now()}-${i}`,
                  question: q.question,
                  options: q.options,
                  correct_option: q.options.indexOf(q.options.find((_, idx) => 
                    String.fromCharCode(65 + idx) === q.correct_answer
                  )),
                  explanation: q.explanation || '',
                  user_answer: q.options.indexOf(q.options.find((_, idx) => 
                    String.fromCharCode(65 + idx) === userAnswer
                  )),
                  source: 'prelims_mcq',
                  subject: topic
                })
              });
            }
          }
        }
      }
    } catch (err) {
      // Silent fail - tracking is non-critical
    }
  };

  const calculateScore = () => {
    if (!result?.questions) return { correct: 0, total: 0 };
    let correct = 0;
    result.questions.forEach((q, idx) => {
      if (userAnswers[idx] === q.correct_answer) {
        correct++;
      }
    });
    return { correct, total: result.questions.length };
  };

  const score = showAnswers ? calculateScore() : null;

  // Get difficulty color
  const getDifficultyColor = (diff) => {
    switch(diff?.toLowerCase()) {
      case 'easy': return 'bg-green-100 text-green-700 border-green-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'hard': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  // Navigate to question
  const goToQuestion = (idx) => {
    setCurrentQuestionIndex(idx);
    // Scroll to question
    document.getElementById(`question-${idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">MCQ Practice</h1>
          <p className="text-sm text-slate-600">
            Generate UPSC-style MCQs on any topic
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="text-xs"
          >
            <History className="w-4 h-4 mr-1" />
            History
          </Button>
          <Badge className={isPro ? "bg-amber-500" : "bg-slate-200"}>
            {isPro ? "⭐ PRO" : "🔓 FREE"}
          </Badge>
        </div>
      </div>

      {/* Performance History Panel */}
      {showHistory && (
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-indigo-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Performance History
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>×</Button>
            </div>
            {performanceHistory.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No practice sessions yet. Start practicing!</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {performanceHistory.slice(0, 5).map((entry, idx) => (
                  <div key={entry.id} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{entry.topic}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(entry.date).toLocaleDateString()} • {entry.difficulty}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold ${entry.percentage >= 70 ? 'text-green-600' : entry.percentage >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                        {entry.percentage}%
                      </p>
                      <p className="text-xs text-slate-500">{entry.score}/{entry.total}</p>
                    </div>
                  </div>
                ))}
                {performanceHistory.length > 5 && (
                  <p className="text-xs text-center text-slate-500">+ {performanceHistory.length - 5} more sessions</p>
                )}
              </div>
            )}
            {performanceHistory.length > 0 && (
              <div className="mt-3 pt-3 border-t grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-lg p-2">
                  <p className="text-lg font-bold text-indigo-600">{performanceHistory.length}</p>
                  <p className="text-xs text-slate-500">Sessions</p>
                </div>
                <div className="bg-white rounded-lg p-2">
                  <p className="text-lg font-bold text-indigo-600">
                    {Math.round(performanceHistory.reduce((a, b) => a + b.percentage, 0) / performanceHistory.length)}%
                  </p>
                  <p className="text-xs text-slate-500">Avg Score</p>
                </div>
                <div className="bg-white rounded-lg p-2">
                  <p className="text-lg font-bold text-green-600">
                    {performanceHistory.filter(p => p.percentage >= 70).length}
                  </p>
                  <p className="text-xs text-slate-500">Good (70%+)</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* MCQ Configuration */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="topic">Topic</Label>
              <Input
                id="topic"
                placeholder="e.g., Fundamental Rights, Fiscal Policy, Climate Change"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="difficulty">Difficulty</Label>
              <select
                id="difficulty"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="easy">🟢 Easy</option>
                <option value="medium">🟡 Medium</option>
                <option value="hard">🔴 Hard</option>
              </select>
            </div>

            <div>
              <Label htmlFor="mcqType">MCQ Type</Label>
              <select
                id="mcqType"
                value={mcqType}
                onChange={(e) => setMcqType(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                <option value="single">Single Statement</option>
                <option value="2-statement">2-Statement</option>
                <option value="3-statement">3-Statement</option>
                <option value="4-statement">4-Statement</option>
                <option value="assertion-reason">Assertion-Reason</option>
              </select>
            </div>

            <div>
              <Label htmlFor="numQuestions">Number of Questions</Label>
              <Input
                id="numQuestions"
                type="number"
                min="1"
                max={isPro ? "50" : "10"}
                value={numQuestions}
                onChange={(e) => setNumQuestions(parseInt(e.target.value) || 10)}
                disabled={loading}
              />
            </div>

            {/* Timer Settings */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="timer" className="flex items-center gap-2">
                  <Timer className="w-4 h-4 text-orange-500" />
                  Timer Mode
                </Label>
                <input
                  type="checkbox"
                  id="timer"
                  checked={timerEnabled}
                  onChange={(e) => setTimerEnabled(e.target.checked)}
                  className="w-4 h-4"
                />
              </div>
              {timerEnabled && (
                <div>
                  <Label className="text-xs text-slate-500">Seconds per question</Label>
                  <select
                    value={timePerQuestion}
                    onChange={(e) => setTimePerQuestion(parseInt(e.target.value))}
                    className="w-full px-3 py-1 border border-slate-300 rounded-md text-sm"
                  >
                    <option value={60}>1 min/question</option>
                    <option value={90}>1.5 min/question</option>
                    <option value={120}>2 min/question (Recommended)</option>
                    <option value={180}>3 min/question</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Daily Limit Indicator for FREE users */}
          {!isPro && (
            <div className={`p-3 rounded-lg border ${dailyUsage >= FREE_DAILY_LIMIT ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-slate-700">
                    Daily Limit: {dailyUsage}/{FREE_DAILY_LIMIT} used
                  </span>
                </div>
                {dailyUsage >= FREE_DAILY_LIMIT ? (
                  <Badge className="bg-red-100 text-red-700 border-0 text-xs">Limit Reached</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">FREE</Badge>
                )}
              </div>
              {dailyUsage >= FREE_DAILY_LIMIT && (
                <p className="text-xs text-red-600 mt-1">
                  Upgrade to PRO for unlimited MCQ generation!
                </p>
              )}
            </div>
          )}

          <Button
            onClick={handleGenerate}
            disabled={loading || (!isPro && dailyUsage >= FREE_DAILY_LIMIT)}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {loadingMessage || "Generating..."}
              </>
            ) : (
              "Generate MCQs"
            )}
          </Button>
        </CardContent>
      </Card>

      {/* MCQ Results */}
      {result?.questions && (
        <div className="space-y-4">
          {/* Timer & Progress Bar */}
          <Card className="border-slate-200 sticky top-0 z-10 bg-white shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                {/* Progress */}
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-sm">
                    Question {currentQuestionIndex + 1} of {result.questions.length}
                  </Badge>
                  <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500 transition-all duration-300"
                      style={{ width: `${((Object.keys(userAnswers).length) / result.questions.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500">
                    {Object.keys(userAnswers).length}/{result.questions.length} answered
                  </span>
                </div>

                {/* Timer Display */}
                {timerEnabled && timerActive && (
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${
                    timeRemaining <= 60 ? 'bg-red-100 text-red-700' : 
                    timeRemaining <= 180 ? 'bg-amber-100 text-amber-700' : 
                    'bg-green-100 text-green-700'
                  }`}>
                    <Clock className="w-4 h-4" />
                    <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
                  </div>
                )}
              </div>

              {/* Question Navigation Grid */}
              <div className="mt-3 pt-3 border-t">
                <Label className="text-xs text-slate-500 mb-2 block">Jump to Question</Label>
                <div className="flex flex-wrap gap-1">
                  {result.questions.map((_, idx) => {
                    const isAnswered = userAnswers[idx] !== undefined;
                    const isCurrent = idx === currentQuestionIndex;
                    const isCorrect = showAnswers && userAnswers[idx] === result.questions[idx].correct_answer;
                    const isWrong = showAnswers && isAnswered && !isCorrect;
                    
                    return (
                      <button
                        key={idx}
                        onClick={() => goToQuestion(idx)}
                        className={`w-8 h-8 rounded-md text-xs font-bold transition-all ${
                          showAnswers 
                            ? isCorrect 
                              ? 'bg-green-500 text-white' 
                              : isWrong 
                                ? 'bg-red-500 text-white'
                                : 'bg-slate-200 text-slate-500'
                            : isCurrent
                              ? 'bg-indigo-600 text-white ring-2 ring-indigo-300'
                              : isAnswered
                                ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Score Display */}
          {showAnswers && score && (
            <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
              <CardContent className="p-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Your Score</p>
                    <p className="text-4xl font-bold text-indigo-600">
                      {score.correct}/{score.total}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Accuracy</p>
                    <p className={`text-4xl font-bold ${
                      (score.correct / score.total) >= 0.7 ? 'text-green-600' :
                      (score.correct / score.total) >= 0.4 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {Math.round((score.correct / score.total) * 100)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Performance</p>
                    <p className="text-2xl font-bold">
                      {(score.correct / score.total) >= 0.7 ? '🏆 Excellent!' :
                       (score.correct / score.total) >= 0.4 ? '👍 Good' : '📚 Keep Practicing'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Questions */}
          {result.questions.map((q, idx) => {
            const userAnswer = userAnswers[idx];
            const isCorrect = userAnswer === q.correct_answer;
            const isAttempted = userAnswer !== undefined;

            return (
              <Card
                key={idx}
                id={`question-${idx}`}
                className={`scroll-mt-32 ${
                  showAnswers && isAttempted
                    ? isCorrect
                      ? "border-green-300 bg-green-50"
                      : "border-red-300 bg-red-50"
                    : currentQuestionIndex === idx ? "ring-2 ring-indigo-300" : ""
                }`}
                onClick={() => setCurrentQuestionIndex(idx)}
              >
                <CardContent className="p-6 space-y-4">
                  {/* Question Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <Badge variant="outline" className="shrink-0">
                        Q{idx + 1}
                      </Badge>
                      <p className="text-base text-slate-900 font-medium">
                        {q.question}
                      </p>
                    </div>
                    {/* Difficulty Badge */}
                    <Badge 
                      variant="outline" 
                      className={`shrink-0 text-xs ${getDifficultyColor(q.difficulty || difficulty)}`}
                    >
                      {(q.difficulty || difficulty) === 'easy' ? '🟢 Easy' : 
                       (q.difficulty || difficulty) === 'medium' ? '🟡 Medium' : '🔴 Hard'}
                    </Badge>
                  </div>

                  {/* Options */}
                  <div className="space-y-2">
                    {Object.entries(q.options).map(([key, value]) => {
                      const isSelected = userAnswer === key;
                      const isCorrectOption = key === q.correct_answer;

                      return (
                        <button
                          key={key}
                          onClick={() => !showAnswers && handleAnswerSelect(idx, key)}
                          disabled={showAnswers}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            showAnswers
                              ? isCorrectOption
                                ? "bg-green-100 border-green-400"
                                : isSelected
                                ? "bg-red-100 border-red-400"
                                : "bg-white border-slate-200"
                              : isSelected
                              ? "bg-indigo-50 border-indigo-400"
                              : "bg-white border-slate-200 hover:border-indigo-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{key})</span>
                              <span>{value}</span>
                            </div>
                            {showAnswers && (
                              <>
                                {isCorrectOption && (
                                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                                )}
                                {isSelected && !isCorrectOption && (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                              </>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Explanation (shown after submission) */}
                  {showAnswers && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-blue-900">
                            Correct Answer: {q.correct_answer}
                          </p>
                          <p className="text-sm text-slate-700">
                            {q.explanation}
                          </p>

                          {/* PRO Features */}
                          {isPro && q.elimination_logic && (
                            <div className="pt-2 border-t border-blue-200">
                              <p className="text-xs font-semibold text-blue-900 mb-1">
                                Elimination Logic:
                              </p>
                              {Object.entries(q.elimination_logic).map(([key, value]) => (
                                <p key={key} className="text-xs text-slate-600">
                                  • {key}: {value}
                                </p>
                              ))}
                            </div>
                          )}

                          {isPro && q.related_facts && (
                            <div className="pt-2 border-t border-blue-200">
                              <p className="text-xs font-semibold text-blue-900 mb-1">
                                Related Facts:
                              </p>
                              {q.related_facts.map((fact, i) => (
                                <p key={i} className="text-xs text-slate-600">
                                  • {fact}
                                </p>
                              ))}
                            </div>
                          )}

                          {isPro && q.reference && (
                            <div className="pt-2 border-t border-blue-200">
                              <p className="text-xs text-slate-600">
                                <span className="font-semibold">Reference:</span> {q.reference}
                              </p>
                            </div>
                          )}
                          
                          <div className="pt-2 border-t border-blue-200 flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`report-question-${idx}`}
                              className={`text-xs ${reportedQuestions.has(idx) ? 'text-red-500' : 'text-slate-400 hover:text-red-500'}`}
                              disabled={reportedQuestions.has(idx)}
                              onClick={(e) => { e.stopPropagation(); handleReportQuestion(q, idx); }}
                            >
                              <Flag className="w-3 h-3 mr-1" />
                              {reportedQuestions.has(idx) ? 'Reported' : 'Report Wrong Answer'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation buttons */}
                  {!showAnswers && (
                    <div className="flex justify-between pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (idx > 0) goToQuestion(idx - 1);
                        }}
                        disabled={idx === 0}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (idx < result.questions.length - 1) goToQuestion(idx + 1);
                        }}
                        disabled={idx === result.questions.length - 1}
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Submit Button */}
          {!showAnswers && (
            <Button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
              size="lg"
            >
              Submit & Check Answers ({Object.keys(userAnswers).length}/{result.questions.length} answered)
            </Button>
          )}

          {/* Reset Button */}
          {showAnswers && (
            <Button
              onClick={() => {
                setResult(null);
                setUserAnswers({});
                setShowAnswers(false);
                setCurrentQuestionIndex(0);
                setTimerActive(false);
              }}
              variant="outline"
              className="w-full"
            >
              Generate New MCQs
            </Button>
          )}
        </div>
      )}

      {/* FREE Limit Message */}
      {result?.message && !isPro && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-amber-900">{result.message}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PrelimsMCQPage;
